/**
 * Packing rules for an item: what one of its unit holds, and the packages it
 * comes in. The rules half mirrors poultry-ms-api/src/modules/inventory/packing.ts
 * word for word, so the form refuses exactly what the server would; a change
 * there needs the same change here. Below them, typing and reading
 * quantities in packages.
 *
 * Quantities are compared as whole ten-thousandths (BigInt), the precision
 * every quantity column has, so "50 × 0.02 = 1" is checked exactly rather
 * than through floating point.
 */

/** Ten-thousandths: Decimal(18,4). */
const PLACES = 4;
const SCALE = 10n ** BigInt(PLACES);

/** Most package sizes one item may have; more is a typo rather than a need. */
export const MAX_PACKAGINGS = 6;

/** Packs inside one package, at most; past this it is not a pack. */
export const MAX_INNER_COUNT = 100_000;

/**
 * A decimal string as whole ten-thousandths, or null when it is not a plain
 * number or has more than four decimal places.
 */
export function toTenThousandths(value: string): bigint | null {
  const m = /^\s*(-)?(\d*)(?:\.(\d*))?\s*$/.exec(value);
  if (!m || (!m[2] && !m[3])) return null;
  const fraction = m[3] ?? '';
  if (fraction.length > PLACES) return null;
  const whole = BigInt(m[2] || '0') * SCALE + BigInt(fraction.padEnd(PLACES, '0') || '0');
  return m[1] ? -whole : whole;
}

/** Whole ten-thousandths back to the shortest decimal string. */
export function fromTenThousandths(value: bigint): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const whole = abs / SCALE;
  const fraction = (abs % SCALE).toString().padStart(PLACES, '0').replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

/** Whether a quantity fits a unit counted to `precision` decimal places. */
export function fitsPrecision(value: bigint, precision: number): boolean {
  const places = Math.max(0, Math.min(PLACES, precision));
  return value % 10n ** BigInt(PLACES - places) === 0n;
}

export interface PackagingInput {
  name: string;
  qtyPerPackage: string;
  innerName?: string | null | undefined;
  innerCount?: number | null | undefined;
  innerQty?: string | null | undefined;
  isDefault?: boolean | undefined;
}

export interface PackingUnit {
  /** As shown to people: "kg", "Bag". */
  label: string;
  /** Other names the same unit goes by (code, name, abbreviation). */
  names: string[];
  precision: number;
}

export interface PackingContext {
  /** The unit the item is counted in. */
  unit: PackingUnit;
  /** The unit `contentQty` is in, when given. */
  contentUnit?: PackingUnit | undefined;
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const isNamedLike = (name: string, unit: PackingUnit) => unit.names.some((n) => n && sameName(n, name));

/**
 * Pack counts on either side of an uneven split, for the form to offer: 50 kg
 * in packs of 3 kg is 16 packs (48 kg) or 17 packs (51 kg). Null when the
 * split is already even or the sizes are unusable.
 */
export function nearestSplits(
  total: string,
  each: string,
): { fewer: { count: number; total: string } | null; more: { count: number; total: string } } | null {
  const t = toTenThousandths(total);
  const e = toTenThousandths(each);
  if (t === null || e === null || t <= 0n || e <= 0n || t % e === 0n) return null;
  const fewer = t / e;
  return {
    fewer: fewer >= 2n ? { count: Number(fewer), total: fromTenThousandths(fewer * e) } : null,
    more: { count: Number(fewer + 1n), total: fromTenThousandths((fewer + 1n) * e) },
  };
}

/**
 * The first problem with what one unit holds, or null when it is fine. Both
 * or neither must be given, and a unit cannot "hold" itself.
 */
export function contentProblem(
  contentQty: string | null | undefined,
  hasContentUnit: boolean,
  context: PackingContext,
): string | null {
  const given = contentQty !== null && contentQty !== undefined && contentQty.trim() !== '';
  if (!given && !hasContentUnit) return null;
  if (!given) return `Say how much one ${context.unit.label} holds, or clear its unit.`;
  if (!hasContentUnit) return `Choose the unit for what one ${context.unit.label} holds.`;
  const qty = toTenThousandths(contentQty);
  if (qty === null || qty <= 0n) return `What one ${context.unit.label} holds must be a number above zero, with at most 4 decimals.`;
  const contentUnit = context.contentUnit;
  if (contentUnit && contentUnit.names.some((n) => context.unit.names.some((m) => n && m && sameName(n, m))))
    return `One ${context.unit.label} cannot hold ${context.unit.label}: choose a different unit, or leave it blank.`;
  return null;
}

/**
 * The first problem with a set of packages, or null when they are fine. The
 * message names the package, so the form can show it beside the right row.
 */
export function packagingProblem(packagings: PackagingInput[], context: PackingContext): string | null {
  const { unit } = context;
  if (packagings.length > MAX_PACKAGINGS) return `An item can have at most ${MAX_PACKAGINGS} package sizes.`;
  if (packagings.filter((p) => p.isDefault).length > 1) return 'Only one package size can be the default.';

  const seen: string[] = [];
  for (const [index, p] of packagings.entries()) {
    const name = p.name?.trim() ?? '';
    const called = name || `Package ${index + 1}`;
    if (!name) return `${called}: give the package a name, such as Bag or Carton.`;
    if (name.length > 32) return `${called}: the name can be at most 32 characters.`;
    if (seen.some((s) => sameName(s, name))) return `There are two packages called ${name}. Give each a different name.`;
    seen.push(name);
    if (isNamedLike(name, unit))
      return `${name} is already the unit this item is counted in. Name the package after what it comes in, such as Bag or Carton.`;
    if (context.contentUnit && isNamedLike(name, context.contentUnit))
      return `${name} is a unit of measure, not a package. Name it after what it comes in, such as Bag or Carton.`;

    const size = toTenThousandths(p.qtyPerPackage ?? '');
    if (size === null || size <= 0n)
      return `${name}: say how many ${unit.label} one ${name} holds, as a number above zero.`;
    if (!fitsPrecision(size, unit.precision))
      return unit.precision === 0
        ? `${name}: ${unit.label} is counted in whole numbers, so one ${name} must hold a whole number of ${unit.label}.`
        : `${name}: ${unit.label} is counted to ${unit.precision} decimal place(s), so ${p.qtyPerPackage} cannot be kept exactly.`;

    const innerName = p.innerName?.trim() ?? '';
    const hasInner = innerName !== '' || (p.innerCount ?? null) !== null || (p.innerQty ?? '') !== '';
    if (!hasInner) continue;
    if (!innerName) return `${name}: name the packs inside, such as Pack or Bottle.`;
    if (innerName.length > 32) return `${name}: the name of the packs inside can be at most 32 characters.`;
    if (sameName(innerName, name)) return `${name}: the packs inside need a different name from the ${name} itself.`;
    if (isNamedLike(innerName, unit))
      return `${name}: ${innerName} is the unit this item is counted in. Leave packs inside off, or name them after what they come in.`;
    const count = p.innerCount;
    if (count === null || count === undefined || !Number.isInteger(count) || count < 2 || count > MAX_INNER_COUNT)
      return `${name}: say how many ${innerName} are inside, a whole number from 2 to ${MAX_INNER_COUNT.toLocaleString('en-US')}.`;
    const each = toTenThousandths(p.innerQty ?? '');
    if (each === null || each <= 0n) return `${name}: say how many ${unit.label} one ${innerName} holds, as a number above zero.`;
    if (!fitsPrecision(each, unit.precision))
      return unit.precision === 0
        ? `${name}: ${unit.label} is counted in whole numbers, so one ${innerName} must hold a whole number of ${unit.label}.`
        : `${name}: ${unit.label} is counted to ${unit.precision} decimal place(s), so ${p.innerQty} cannot be kept exactly.`;
    if (BigInt(count) * each !== size) {
      const made = fromTenThousandths(BigInt(count) * each);
      return `${name}: ${count} × ${innerName} of ${fromTenThousandths(each)} ${unit.label} is ${made} ${unit.label}, not the ${fromTenThousandths(size)} ${unit.label} one ${name} holds.`;
    }
  }
  return null;
}

/**
 * The packages as they are stored: names trimmed, inner packs all-or-nothing,
 * sizes in canonical form, and exactly one default when there is any package
 * (the first, when none was chosen).
 */
export function normalisePackagings(packagings: PackagingInput[]) {
  const anyDefault = packagings.some((p) => p.isDefault);
  return packagings.map((p, index) => {
    const innerName = p.innerName?.trim() || null;
    return {
      name: p.name.trim(),
      qtyPerPackage: fromTenThousandths(toTenThousandths(p.qtyPerPackage)!),
      innerName,
      innerCount: innerName ? (p.innerCount ?? null) : null,
      innerQty: innerName && p.innerQty ? fromTenThousandths(toTenThousandths(p.innerQty)!) : null,
      isDefault: anyDefault ? !!p.isDefault : index === 0,
      sortOrder: index,
    };
  });
}

// ---------------------------------------------------------------------------
// Typing and reading quantities in packages. Stock is always stored in the
// item's own unit; these only translate to and from what is on the shelf.

/** The packing an item carries, as the item list sends it. */
export interface PackedItem {
  uom: string;
  uomPrecision: number;
  contentQty?: string | null | undefined;
  contentUom?: string | null | undefined;
  contentUomPrecision?: number | null | undefined;
  packagings?: readonly ItemPackaging[] | undefined;
}

export interface ItemPackaging {
  id: string;
  name: string;
  qtyPerPackage: string;
  innerName: string | null;
  innerCount: number | null;
  innerQty: string | null;
  isDefault: boolean;
}

/** One way a quantity can be typed: the item's unit, a package, or a pack inside one. */
export interface EntryUnit {
  key: string;
  /** Singular, as typed after a number: "Carton", "KG". */
  label: string;
  /** The item's unit in one of these, in ten-thousandths. */
  size: bigint;
  /** The package it is, or holds the packs of. */
  packaging?: ItemPackaging | undefined;
  /** "Carton + Pack": a whole-package count and a loose-pack count together. */
  withPacks?: boolean | undefined;
}

export const UNIT_KEY = 'unit';

/**
 * Every way to type a quantity of the item: its own unit first, then each
 * package, the packs inside it, and package plus packs together. A pack that
 * two packages share (the same name and size) is offered once.
 */
export function entryUnits(item: PackedItem): EntryUnit[] {
  const units: EntryUnit[] = [{ key: UNIT_KEY, label: item.uom, size: SCALE }];
  const inners: EntryUnit[] = [];
  for (const p of item.packagings ?? []) {
    const size = toTenThousandths(p.qtyPerPackage);
    if (size === null || size <= 0n) continue;
    units.push({ key: `pkg:${p.id}`, label: p.name, size, packaging: p });
    const innerSize = p.innerQty ? toTenThousandths(p.innerQty) : null;
    if (p.innerName && innerSize && innerSize > 0n) {
      units.push({ key: `mix:${p.id}`, label: `${p.name} + ${p.innerName}`, size, packaging: p, withPacks: true });
      if (!inners.some((u) => sameName(u.label, p.innerName!) && u.size === innerSize))
        inners.push({ key: `inner:${p.id}`, label: p.innerName, size: innerSize, packaging: p });
    }
  }
  return [...units, ...inners];
}

/** The unit Stock IN offers first: the default package, else the item's unit. */
export function defaultEntryUnit(item: PackedItem): EntryUnit {
  const units = entryUnits(item);
  const def = item.packagings?.find((p) => p.isDefault);
  return units.find((u) => def && u.key === `pkg:${def.id}`) ?? units[0]!;
}

/** Singular below or at one, plural above: "1 Carton", "3 Cartons". Units of measure stay as they are. */
export function plural(label: string, count: bigint | number, isUnit = false): string {
  if (isUnit) return label;
  const n = typeof count === 'bigint' ? count : BigInt(Math.trunc(count));
  if (n === 1n || n === -1n) return label;
  if (/[^aeiou]y$/i.test(label)) return `${label.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(label)) return `${label}es`;
  return `${label}s`;
}

const unitText = (amount: string, unit: EntryUnit, isUnit: boolean) => {
  const scaled = toTenThousandths(amount) ?? 0n;
  return `${amount} ${plural(unit.label, scaled === SCALE ? 1n : 2n, isUnit)}`;
};

/**
 * A typed amount in the item's unit, or the reason it cannot be kept. The
 * amount itself may have up to four decimals ("1.5 Cartons"), but the result
 * must be a quantity the item's unit can count; nothing is rounded silently.
 */
export function toItemQty(
  item: PackedItem,
  unit: EntryUnit,
  amount: string,
  packs = '',
): { qty: string } | { problem: string } | null {
  const typed = amount.trim();
  const typedPacks = unit.withPacks ? packs.trim() : '';
  if (typed === '' && typedPacks === '') return null;
  const whole = typed === '' ? 0n : toTenThousandths(typed);
  if (whole === null || whole < 0n) return { problem: 'Type a number of zero or more, with at most 4 decimals.' };
  let total = whole * unit.size; // in 10^-8 of the item's unit
  if (unit.withPacks) {
    const loose = typedPacks === '' ? 0n : toTenThousandths(typedPacks);
    if (loose === null || loose < 0n) return { problem: 'Type a number of packs of zero or more.' };
    total += loose * toTenThousandths(unit.packaging!.innerQty!)!;
  }
  const isUnit = unit.key === UNIT_KEY;
  if (total % SCALE !== 0n) {
    return { problem: `That is more decimals of ${item.uom} than can be kept. Type it in ${item.uom} instead.` };
  }
  const qty = total / SCALE;
  if (!fitsPrecision(qty, item.uomPrecision)) {
    const said = unit.withPacks
      ? `${typed || '0'} ${plural(unit.packaging!.name, 2n)} + ${typedPacks || '0'} ${plural(unit.packaging!.innerName!, 2n)}`
      : unitText(typed, unit, isUnit);
    return {
      problem:
        item.uomPrecision === 0
          ? `${said} is ${fromTenThousandths(qty)} ${item.uom}, and ${item.uom} is counted in whole numbers.`
          : `${said} is ${fromTenThousandths(qty)} ${item.uom}, more decimals than ${item.uom} keeps (${item.uomPrecision}).`,
    };
  }
  return { qty: fromTenThousandths(qty) };
}

/**
 * A quantity in the item's unit as typed in `unit`, when it divides exactly;
 * null when it does not ("7 KG" is not a whole number of 50 kg Cartons).
 */
export function fromItemQty(qty: string, unit: EntryUnit): { amount: string; packs: string } | null {
  const q = toTenThousandths(qty);
  if (q === null) return null;
  if (unit.key === UNIT_KEY) return { amount: fromTenThousandths(q), packs: '' };
  if (unit.withPacks) {
    const inner = toTenThousandths(unit.packaging!.innerQty!)!;
    const full = q / unit.size;
    const rest = q - full * unit.size;
    if (rest % inner !== 0n) return null;
    return { amount: full.toString(), packs: (rest / inner).toString() };
  }
  const scaled = q * SCALE;
  if (scaled % unit.size !== 0n) return null;
  return { amount: fromTenThousandths(scaled / unit.size), packs: '' };
}

/**
 * A quantity broken into the default package and its packs, the rest in the
 * item's unit: "3 Cartons + 20 Packs", "1 Bag + 12.5 KG". Null when there is
 * no package or the quantity is less than one of its smallest part, so the
 * plain quantity says it all.
 */
export function packedBreakdown(qty: string, item: PackedItem): string | null {
  const def = item.packagings?.find((p) => p.isDefault) ?? item.packagings?.[0];
  const q = toTenThousandths(qty);
  if (!def || q === null || q === 0n) return null;
  const size = toTenThousandths(def.qtyPerPackage);
  if (!size || size <= 0n) return null;
  const negative = q < 0n;
  let rest = negative ? -q : q;
  const parts: string[] = [];
  const full = rest / size;
  rest -= full * size;
  if (full > 0n) parts.push(`${full} ${plural(def.name, full)}`);
  const inner = def.innerName && def.innerQty ? toTenThousandths(def.innerQty) : null;
  if (inner && inner > 0n) {
    const packs = rest / inner;
    rest -= packs * inner;
    if (packs > 0n) parts.push(`${packs} ${plural(def.innerName!, packs)}`);
  }
  if (parts.length === 0) return null;
  if (rest > 0n) parts.push(`${fromTenThousandths(rest)} ${item.uom}`);
  return `${negative ? '−' : ''}${parts.join(' + ')}`;
}

/** What a quantity holds, when one of the item's unit holds something: "6,900 KG". */
export function contentTotal(qty: string, item: PackedItem, format: (value: string, precision: number) => string): string | null {
  if (!item.contentQty || !item.contentUom) return null;
  const q = toTenThousandths(qty);
  const each = toTenThousandths(item.contentQty);
  if (q === null || each === null) return null;
  const total = q * each; // 10^-8
  const rounded = (total + (total >= 0n ? SCALE / 2n : -SCALE / 2n)) / SCALE;
  return `${format(fromTenThousandths(rounded), item.contentUomPrecision ?? 2)} ${item.contentUom}`;
}

/**
 * A price per `unit` as a price per one of the item's unit, kept to four
 * decimals as costs are stored: Rs 5,000 a 50 kg Bag is Rs 100 a KG.
 */
export function costPerItemUnit(cost: string, unit: EntryUnit): string | null {
  const c = toTenThousandths(cost);
  if (c === null) return null;
  if (unit.withPacks || unit.key === UNIT_KEY) return fromTenThousandths(c);
  const scaled = c * SCALE * 2n;
  const q = scaled / unit.size;
  return fromTenThousandths((q + (q >= 0n ? 1n : -1n)) / 2n);
}

/** A price per one of the item's unit as a price per `unit`. */
export function costPerEntryUnit(cost: string, unit: EntryUnit): string | null {
  const c = toTenThousandths(cost);
  if (c === null) return null;
  if (unit.withPacks || unit.key === UNIT_KEY) return fromTenThousandths(c);
  const total = c * unit.size; // 10^-8
  return fromTenThousandths((total + SCALE / 2n) / SCALE);
}

/**
 * Pack counts near `around` that split `total` evenly into packs of at most
 * two decimals the unit can count: 50 KG near 3 packs gives 2 (25 KG) and 5
 * (10 KG). For when the typed count leaves a fraction of a pack.
 */
export function evenCounts(
  total: string,
  around: number,
  precision: number,
): { below: { count: number; each: string } | null; above: { count: number; each: string } | null } | null {
  const t = toTenThousandths(total);
  if (t === null || t <= 0n || !Number.isFinite(around)) return null;
  const nice = Math.min(2, Math.max(0, precision));
  const fits = (n: number) => {
    if (t % BigInt(n) !== 0n) return null;
    const each = t / BigInt(n);
    return fitsPrecision(each, nice) ? { count: n, each: fromTenThousandths(each) } : null;
  };
  let below: { count: number; each: string } | null = null;
  for (let n = Math.min(Math.floor(around), MAX_INNER_COUNT); n >= 2 && !below; n--) if (n !== around) below = fits(n);
  let above: { count: number; each: string } | null = null;
  const limit = Math.min(MAX_INNER_COUNT, Math.max(Math.ceil(around) * 4, 100));
  for (let n = Math.max(2, Math.ceil(around)); n <= limit && !above; n++) if (n !== around) above = fits(n);
  return below || above ? { below, above } : null;
}

/** What kind of unit an item is counted in, which decides what the packing card offers. */
export type UnitFamily = 'weight' | 'volume' | 'dose' | 'piece' | 'container' | 'other';

export function unitFamily(...names: (string | null | undefined)[]): UnitFamily {
  const said = names.filter((n): n is string => !!n).map((n) => n.trim().toLowerCase());
  const is = (re: RegExp) => said.some((n) => re.test(n));
  if (is(/^(kg|kgs|kilo|kilos|kilogram|kilograms|g|gm|gms|gram|grams|ton|tons|tonne|tonnes|mt|lb|lbs)$/)) return 'weight';
  if (is(/^(l|lt|ltr|ltrs|litre|litres|liter|liters|ml|millilitre|millilitres|milliliter|milliliters)$/)) return 'volume';
  if (is(/^(dose|doses|ds)$/)) return 'dose';
  if (is(/^(pc|pcs|piece|pieces|nos|no|each|ea|unit|units|number)$/)) return 'piece';
  if (
    is(/^(bag|bags|bori|pack|packs|packet|packets|pkt|carton|cartons|ctn|box|boxes|bottle|bottles|btl|can|cans|tin|tins|tray|trays|sack|sacks|drum|drums|jar|jars|vial|vials|pouch|pouches|sachet|sachets|bundle|bundles|roll|rolls|bucket|buckets|tub|tubs)$/)
  )
    return 'container';
  return 'other';
}

/** One package in a few words: "Bag of 50 KG", "Carton of 50 × 1 KG". */
export function packageLabel(p: ItemPackaging, uom: string): string {
  const size = fromTenThousandths(toTenThousandths(p.qtyPerPackage) ?? 0n);
  if (p.innerName && p.innerCount && p.innerQty) {
    const each = fromTenThousandths(toTenThousandths(p.innerQty) ?? 0n);
    return `${p.name} of ${p.innerCount} × ${each} ${uom}`;
  }
  return `${p.name} of ${size} ${uom}`;
}

/** The whole of one package spelt out: "1 Carton = 50 Packs × 1 KG = 50 KG = 50 KG". */
export function packageSentence(p: ItemPackaging, item: PackedItem): string {
  const size = toTenThousandths(p.qtyPerPackage) ?? 0n;
  const inner =
    p.innerName && p.innerCount && p.innerQty
      ? `${p.innerCount} ${plural(p.innerName, p.innerCount)} × ${fromTenThousandths(toTenThousandths(p.innerQty) ?? 0n)} ${item.uom} = `
      : '';
  const content = item.contentQty && item.contentUom ? toTenThousandths(item.contentQty) : null;
  const holds = content ? ` = ${fromTenThousandths((size * content + SCALE / 2n) / SCALE)} ${item.contentUom}` : '';
  return `1 ${p.name} = ${inner}${fromTenThousandths(size)} ${item.uom}${holds}`;
}

/** "One BAG holds 50 KG", or null. */
export function holdsSentence(item: PackedItem): string | null {
  if (!item.contentQty || !item.contentUom) return null;
  return `One ${item.uom} holds ${fromTenThousandths(toTenThousandths(item.contentQty) ?? 0n)} ${item.contentUom}`;
}

/** " (2 Cartons + 20 Packs)" after a quantity that has a package breakdown, else "". */
export function packedSuffix(qty: string | number, item: PackedItem | null | undefined): string {
  if (!item) return '';
  const text = typeof qty === 'number' ? (Number.isFinite(qty) ? qty.toFixed(4) : '') : qty;
  const breakdown = text ? packedBreakdown(text, item) : null;
  return breakdown ? ` (${breakdown})` : '';
}

/** The package a cost is typed per: a package for "Carton + Pack", else the unit itself. */
export function costUnitOf(unit: EntryUnit): EntryUnit {
  if (!unit.withPacks || !unit.packaging) return unit;
  return { key: `pkg:${unit.packaging.id}`, label: unit.packaging.name, size: unit.size, packaging: unit.packaging };
}

/**
 * An item's packages. An item list kept on a PC from before packages existed
 * has none at all, rather than an empty list, so every reader goes through here.
 */
export function packagingsOf(item: Pick<PackedItem, 'packagings'> | null | undefined): readonly ItemPackaging[] {
  return item?.packagings ?? [];
}
